import { Component, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DataService } from '../../services/data.service';

// ──────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────
export type ContactType  = 'Management'|'Teaching Staff'|'Non-Teaching Staff'|'Student'|'Parent'|'Vendor'|'Emergency'|'Government'|'Alumni'|'Other';
export type DirTab       = 'directory'|'groups'|'emergency'|'manage';
export type ViewMode     = 'grid'|'list';

export interface DirContact {
  id:               number;
  name:             string;
  designation:      string;
  department:       string;
  contact_type:     ContactType;
  phone_primary:    string;
  phone_secondary:  string;
  phone_office:     string;
  email:            string;
  extension:        string;
  whatsapp:         string;
  is_favorite:      boolean;
  is_active:        boolean;
  notes:            string;
}

// ──────────────────────────────────────────────────────────
// COMPONENT
// ──────────────────────────────────────────────────────────
@Component({
  selector:    'app-telephone-directory',
  standalone:  true,
  imports:     [CommonModule, FormsModule],
  templateUrl: './telephone-directory.html',
  styleUrl:    './telephone-directory.scss',
})
export class TelephoneDirectory {
  base = 'phone_dir';		// important for API calls
  loading = false;

  dataService = inject(DataService);

  activeTab    = signal<DirTab>('manage');
  viewMode     = signal<ViewMode>('grid');
  toast        = signal('');
  copied       = signal('');

  showModal    = signal(false);
  contactForm: Partial<DirContact> = {};
  editingId    = signal<number | null>(null);
  formSubmitted = false;

  // ── Filters ───────────────────────────────────────────────
  search      = signal('');
  typeFilter  = signal<ContactType | ''>('');
  deptFilter  = signal('');
  showFavOnly = signal(false);
  manageSearch = signal('');

  private nextId = 36;
  contacts = signal<DirContact[]>(this.buildContacts());

  // ── Computed ──────────────────────────────────────────────
  readonly stats = computed(() => {
    const c = this.contacts().filter(x => x.is_active);
    return {
      total:       c.length,
      teaching:    c.filter(x => x.contact_type === 'Teaching Staff').length,
      nonTeaching: c.filter(x => x.contact_type === 'Non-Teaching Staff').length,
      management:  c.filter(x => x.contact_type === 'Management').length,
      emergency:   c.filter(x => x.contact_type === 'Emergency').length,
      favorites:   c.filter(x => x.is_favorite).length,
    };
  });

  readonly filteredContacts = computed(() => {
    const q = this.search().toLowerCase().trim();
    return this.contacts()
      .filter(c => c.is_active)
      .filter(c => !this.showFavOnly()  || c.is_favorite)
      .filter(c => !this.typeFilter()   || c.contact_type === this.typeFilter())
      .filter(c => !this.deptFilter()   || c.department === this.deptFilter())
      .filter(c => !q || [c.name, c.designation, c.department, c.phone_primary,
                          c.phone_secondary, c.phone_office, c.email, c.extension]
                          .some(v => v.toLowerCase().includes(q)))
      .sort((a, b) => a.name.localeCompare(b.name));
  });

  readonly alphabetGroups = computed(() => {
    const contacts = this.filteredContacts();
    const letters  = [...new Set(contacts.map(c => c.name[0].toUpperCase()))].sort();
    return letters.map(l => ({ letter: l, contacts: contacts.filter(c => c.name.toUpperCase().startsWith(l)) }));
  });

  readonly groupedByType = computed(() => {
    const order: ContactType[] = ['Management','Teaching Staff','Non-Teaching Staff','Student','Parent','Vendor','Emergency','Government','Alumni','Other'];
    return order
      .map(type => ({
        type,
        contacts: this.contacts().filter(c => c.contact_type === type && c.is_active)
                                  .sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .filter(g => g.contacts.length > 0);
  });

  readonly emergencyContacts = computed(() =>
    this.contacts().filter(c => c.contact_type === 'Emergency' && c.is_active)
  );

  readonly favorites = computed(() =>
    this.contacts().filter(c => c.is_favorite && c.is_active).sort((a, b) => a.name.localeCompare(b.name))
  );

  readonly manageList = computed(() => {
    const q = this.manageSearch().toLowerCase();
    return this.contacts()
      .filter(c => !q || [c.name, c.designation, c.department, c.phone_primary].some(v => v.toLowerCase().includes(q)))
      .sort((a, b) => {
        if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
  });

  readonly uniqueDepts = computed(() =>
    [...new Set(this.contacts().filter(c => c.is_active && c.department).map(c => c.department))].sort()
  );
  readonly uniqueTypes = computed(() =>
    [...new Set(this.contacts().filter(c => c.is_active).map(c => c.contact_type))] as ContactType[]
  );
  readonly departmentGroups = computed(() => {
    const depts = [...new Set(this.contacts().filter(c => c.is_active && c.department && c.contact_type !== 'Emergency' && c.contact_type !== 'Vendor').map(c => c.department))].sort();
    return depts.map(dept => ({
      dept,
      contacts: this.contacts().filter(c => c.department === dept && c.is_active).sort((a, b) => a.name.localeCompare(b.name)),
    }));
  });

  async ngOnInit() {
		this.loading = true;
    await this.dataService.getAllRecords(this.base).then((res: any) => {
      // this.allData = res;
      for(let i=0; i<res.length; i++) {
        this.contacts.update(l => [...l, res[i]]);
      }

      this.loading = false;
    });
  }

  // ── CRUD ──────────────────────────────────────────────────
  openForm(contact?: DirContact): void {
    this.contactForm = contact ? { ...contact } : {
      contact_type: 'Teaching Staff', is_favorite: false, is_active: true,
      phone_primary: '', phone_secondary: '', phone_office: '',
      email: '', extension: '', whatsapp: '', notes: '',
    };
    this.editingId.set(contact?.id ?? null);
    this.formSubmitted = false;
    this.showModal.set(true);
  }

  saveContact(): void {
    this.formSubmitted = true;
    if (!this.contactForm.name?.trim() || !this.contactForm.phone_primary?.trim()) return;

    const id = this.editingId();
    if (id) {
      this.contacts.update(l => l.map(c => c.id === id ? { ...c, ...this.contactForm } as DirContact : c));
      this.flash('Contact updated.');
    } else {
      const nc: DirContact = {
        id: this.nextId++,
        name:            this.contactForm.name!,
        designation:     this.contactForm.designation      ?? '',
        department:      this.contactForm.department       ?? '',
        contact_type:    this.contactForm.contact_type     ?? 'Other',
        phone_primary:   this.contactForm.phone_primary!,
        phone_secondary: this.contactForm.phone_secondary  ?? '',
        phone_office:    this.contactForm.phone_office     ?? '',
        email:           this.contactForm.email            ?? '',
        extension:       this.contactForm.extension        ?? '',
        whatsapp:        this.contactForm.whatsapp         ?? '',
        is_favorite:     this.contactForm.is_favorite      ?? false,
        is_active:       true,
        notes:           this.contactForm.notes            ?? ''
      };

      this.doSave(nc);
      this.contacts.update(l => [...l, nc]);
      this.flash('Contact added.');
    }
    this.showModal.set(false);
  }

  toggleActive(id: number): void {
    this.contacts.update(l => l.map(c => c.id === id ? { ...c, is_active: !c.is_active } : c));
  }

  toggleFavorite(id: number): void {
    this.contacts.update(l => l.map(c => c.id === id ? { ...c, is_favorite: !c.is_favorite } : c));
  }

  // ── Actions ───────────────────────────────────────────────
  copyPhone(phone: string): void {
    navigator.clipboard.writeText(phone)
      .then(() => { this.copied.set(phone); this.flash(`Copied: ${phone}`); setTimeout(() => this.copied.set(''), 1500); })
      .catch(() => this.flash('Copy failed.'));
  }

  getCallLink(phone: string): string    { return `tel:${phone.replace(/\s/g, '')}`; }
  getWALink(phone: string): string      { return `https://wa.me/91${phone.replace(/\D/g, '')}`; }
  getEmailLink(email: string): string   { return `mailto:${email}`; }

  // ── Helpers ───────────────────────────────────────────────
  getInitials(name: string): string {
    return name.split(' ').filter(w => /\w/.test(w[0])).map(w => w[0]).slice(0, 2).join('').toUpperCase();
  }
  getTypeColor(type: ContactType): string {
    const m: Record<ContactType,string> = {
      Management: '#1a3a6b', 'Teaching Staff': '#059669', 'Non-Teaching Staff': '#d97706',
      Student: '#7c3aed', Parent: '#0891b2', Vendor: '#6b7280', Emergency: '#dc2626',
      Government: '#374151', Alumni: '#8b5cf6', Other: '#9ca3af',
    };
    return m[type] ?? '#6b7280';
  }
  getTypeBg(type: ContactType): string {
    const m: Record<ContactType,string> = {
      Management: '#dbeafe', 'Teaching Staff': '#dcfce7', 'Non-Teaching Staff': '#fef3c7',
      Student: '#ede9fe', Parent: '#cffafe', Vendor: '#f3f4f6', Emergency: '#fee2e2',
      Government: '#f3f4f6', Alumni: '#f5f3ff', Other: '#f3f4f6',
    };
    return m[type] ?? '#f3f4f6';
  }
  isEmergencyShort(phone: string): boolean { return phone.length <= 3; }
  hasFormErrors(): boolean {
    return this.formSubmitted && (!this.contactForm.name?.trim() || !this.contactForm.phone_primary?.trim());
  }
  formatDate(s: string): string {
    if (!s) return '—';
    return new Date(s).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
  }
  private flash(m: string): void { this.toast.set(m); setTimeout(() => this.toast.set(''), 2500); }
  private isoDate(d: number): string { const dt = new Date(); dt.setDate(dt.getDate() - d); return dt.toISOString(); }

  readonly contactTypes: ContactType[] = ['Management','Teaching Staff','Non-Teaching Staff','Student','Parent','Vendor','Emergency','Government','Alumni','Other'];

  // ──────────────────────────────────────────────────────────
  // SEED DATA
  // ──────────────────────────────────────────────────────────
  private buildContacts(): DirContact[] {
    const c = (id:number, name:string, desig:string, dept:string, type:ContactType,
               ph1:string, ph2:string, pho:string, email:string, ext:string,
               wa:string, fav:boolean): DirContact => ({
      id, name, designation:desig, department:dept, contact_type:type,
      phone_primary:ph1, phone_secondary:ph2, phone_office:pho,
      email, extension:ext, whatsapp:wa,
      is_favorite:fav, is_active:true, notes:''
    });
    return [
      // ── Emergency ────────────────────────────────────────
      // c(1, 'Ambulance',           'National Emergency - Medical', 'Emergency',    'Emergency',  '108',       '',           '',            '',          '',   '',   true),
      // c(2, 'Police Control Room', 'National Emergency - Police',  'Emergency',    'Emergency',  '112',       '',           '',            '',          '',   '',   true),
      // c(3, 'Fire Brigade',        'National Emergency - Fire',    'Emergency',    'Emergency',  '101',       '',           '',            '',          '',   '',   true),
      // c(4, 'City Ambulance',      'Local Ambulance Service',      'Emergency',    'Emergency',  '1916',      '9845100200', '',            '',          '',   '',   false),
      // c(5, 'Electricity Board',   'Power Emergency Helpline',     'Emergency',    'Emergency',  '1912',      '',           '',            '',          '',   '',   false),
      // c(6, 'Women and Children',  'Women and Children Helpline',  'Emergency',    'Emergency',  '1091',      '',           '',            '',          '',   '',   false),
    ];
  }


  doSave(frmData: any) {
    this.loading = true;
    this.dataService.addNewRecord(this.base, frmData).subscribe({
      next: (response: any) => {
        console.log('Response received in component:', response);
        // You can handle your loading states or form resets here:
        this.loading = false;
        // this.resetForm();
      },
      error: (error: any) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  doUpdate() {
    this.loading = true;
    const myid: number = this.contactForm?.id || 0;
    delete this.contactForm.id;

    this.dataService.updateRecord(this.base, myid, this.contactForm).subscribe({
      next: (response) => {
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.resetForm();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
  }

  async deleteContact(id: number) {
    const confirmed = await this.dataService.confirmDelete();
    if (confirmed) {
        // console.log('Item deleted successfully');
				this.doDelete(id);
    } else {
        console.log('Deletion cancelled');
    }
	}

  doDelete(myid: number) {
		this.loading = true;
    this.dataService.deleteRecord(this.base, myid).subscribe({
      next: (response) => {
        this.contacts.update(l => l.filter(c => c.id !== myid));
        this.flash('Contact deleted.');
        // console.log('Response received in component:', response);
        this.loading = false;
        // this.ngOnInit();
      },
      error: (error) => {
        console.error('Error received in component:', error);
        this.loading = false;
      }
    });
	}
}
